/**
 * versona_route — the client half of the route-ahead row (2026-09-15, the
 * versona review's item 2; the server builds the points in
 * server/src/lib/versona_route.ts, schema/environment.ts explains the row).
 *
 * The hot row is a position every ≥ 3.5 s. The crowd used to glide a straight
 * line from where it was drawn to the LAST sample over the observed interval:
 * one sample behind, every corner cut, a dead stop at each row until the next
 * landed. With the route row the body WALKS A POLYLINE — from where it stands,
 * through the row's position, along the corners the server will take — at the
 * server's own pace (stepM per observed row interval), so it is still
 * mid-stride when the next row lands and turns where the street turns. The row
 * remains the authority: every new row rebuilds the walk from the body's
 * current point, and a body already on the new route continues from its
 * projection instead of walking back to the origin. Pure — the harness pins it.
 */

export interface RouteRowLike { x0: number; z0: number; x1: number; z1: number; x2: number; z2: number; x3: number; z3: number; n: number; stepM: number }
export type RoutePt = [number, number];

/** A body this close (m) to the new route is ON it — continue from the projection, never walk back to the origin. */
export const ROUTE_SNAP_M = 1.0;
/** The walk pace is stepM over the observed row interval, clamped to a believable walk (m/s). */
export const ROUTE_PACE_MIN = 0.8;
export const ROUTE_PACE_MAX = 2.6;
/** A route whose origin sits farther than this (m) from the hot row is stale — ignore it. */
export const ROUTE_ORIGIN_TOL_M = 0.05;
/**
 * THE HORIZON: the forecast may run at most this many steps PAST the row's origin
 * before it waits for the next row. A route is a forecast of the server's next
 * step, not a licence to walk the whole leg — a versona the server stopped (a
 * refused slope, a blocked door, an arrival) drops its route row, but between
 * that and the next hot row the body must not sail 50 m down the street.
 */
export const ROUTE_HORIZON_STEPS = 1.25;

/** The polyline a route row describes: the origin, then its n points. */
export function routePoints(r: RouteRowLike): RoutePt[] {
  const pts: RoutePt[] = [[r.x0, r.z0]];
  const n = Math.max(1, Math.min(3, r.n | 0));
  const xs = [r.x1, r.x2, r.x3], zs = [r.z1, r.z2, r.z3];
  for (let i = 0; i < n; i++) pts.push([xs[i], zs[i]]);
  return pts;
}

export function polylineLength(pts: readonly RoutePt[]): number {
  let L = 0;
  for (let i = 1; i < pts.length; i++) L += Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]);
  return L;
}

/** The point at arc length s along the polyline (clamped to its ends) and the heading there (yaw = atan2(dx, dz), the crowd's convention). */
export function pointAlong(pts: readonly RoutePt[], s: number): { x: number; z: number; yaw: number; done: boolean } {
  if (pts.length === 0) return { x: 0, z: 0, yaw: 0, done: true };
  if (pts.length === 1) return { x: pts[0][0], z: pts[0][1], yaw: 0, done: true };
  let rem = Math.max(0, s);
  let yaw = 0;
  for (let i = 1; i < pts.length; i++) {
    const dx = pts[i][0] - pts[i - 1][0], dz = pts[i][1] - pts[i - 1][1];
    const len = Math.hypot(dx, dz);
    if (len > 1e-9) yaw = Math.atan2(dx, dz);
    if (rem <= len || i === pts.length - 1) {
      const f = len > 1e-9 ? Math.min(1, rem / len) : 1;
      return { x: pts[i - 1][0] + dx * f, z: pts[i - 1][1] + dz * f, yaw, done: rem >= len && i === pts.length - 1 };
    }
    rem -= len;
  }
  const last = pts[pts.length - 1];
  return { x: last[0], z: last[1], yaw, done: true };
}

/** The arc length of the nearest point on the polyline to (bx, bz), or -1 when it lies farther than snapM off the line. */
export function projectOnto(pts: readonly RoutePt[], bx: number, bz: number, snapM = ROUTE_SNAP_M): number {
  let best = -1, bestD = snapM, acc = 0;
  for (let i = 1; i < pts.length; i++) {
    const ax = pts[i - 1][0], az = pts[i - 1][1];
    const dx = pts[i][0] - ax, dz = pts[i][1] - az;
    const len2 = dx * dx + dz * dz;
    const t = len2 > 1e-12 ? Math.max(0, Math.min(1, ((bx - ax) * dx + (bz - az) * dz) / len2)) : 0;
    const px = ax + dx * t, pz = az + dz * t;
    const d = Math.hypot(bx - px, bz - pz);
    if (d <= bestD) { bestD = d; best = acc + Math.sqrt(len2) * t; }
    acc += Math.sqrt(len2);
  }
  return best;
}

/**
 * The walk for a new row: the polyline the body follows from where it is
 * drawn. A body already on the route (within snapM) continues from its
 * projection — the common case, since the last walk was heading here — and
 * a body off it first walks to the origin (the hot row) and on along the route.
 */
export function walkFor(route: readonly RoutePt[], bx: number, bz: number, snapM = ROUTE_SNAP_M): { pts: RoutePt[]; s0: number; sOrigin: number } {
  const s = projectOnto(route, bx, bz, snapM);
  if (s >= 0) return { pts: route.slice(), s0: s, sOrigin: 0 };
  return { pts: [[bx, bz], ...route], s0: 0, sOrigin: Math.hypot(route[0][0] - bx, route[0][1] - bz) };
}

/** The arc length the walk may reach before the next row: the origin plus ROUTE_HORIZON_STEPS steps, never past the end. */
export function routeHorizon(sOrigin: number, stepM: number, length: number): number {
  return Math.min(length, sOrigin + stepM * ROUTE_HORIZON_STEPS);
}

/** The pace (m/s): the server's step per observed row interval, clamped to a walk. */
export function routePace(stepM: number, intervalMs: number): number {
  const v = stepM / Math.max(1, intervalMs) * 1000;
  return Math.max(ROUTE_PACE_MIN, Math.min(ROUTE_PACE_MAX, v));
}

/** A route row belongs to a hot row only when its origin IS that row (they are written together). */
export function routeMatchesRow(r: RouteRowLike, curX: number, curZ: number): boolean {
  return Math.abs(r.x0 - curX) <= ROUTE_ORIGIN_TOL_M && Math.abs(r.z0 - curZ) <= ROUTE_ORIGIN_TOL_M;
}
